import { apiClient } from '@/auth';

/**
 * The dashboard data shapes here come from two sources:
 *   - exercises: the dedicated `/exercises` (GET) and `/exercise` (POST) endpoints
 *   - weight + blood_pressure: a single bulk endpoint at
 *     `PUT /patients/{id}/health_data/bulk` (writes), and `/health_data_table`
 *     (reads, generic ApiTable shape — see swagger.yaml:15675)
 *
 * The READ side for weight/BP is currently best-effort — `/health_data_table`
 * returns a generic ApiTable with category-specific row keys, and we don't
 * want to crash if the row shape varies. We try, and fall back to empty.
 */

export type WeightEntry = {
  date: string;
  weight: number;
  unit?: string;
  note?: string | null;
};

export type BloodPressureEntry = {
  date: string;
  systolic: number;
  diastolic: number;
  note?: string | null;
};

export const EXERCISE_TYPES = [
  'aerobic training',
  'strength-resistance',
  'balance and stability',
  'flexibility-stretching',
  'mixed cardio/strength',
  'other',
] as const;

export type ExerciseType = (typeof EXERCISE_TYPES)[number];

export type ExerciseEntry = {
  id: number;
  exercise_date: string;
  exercise_time: string;
  exercise_type: string;
  duration_min: number;
  intensity?: string | null;
  note?: string | null;
};

export type CreateWeightInput = {
  weight_date?: string;
  weight_time?: string;
  weight_kg: number;
  note?: string | null;
};

export type CreateBloodPressureInput = {
  blood_pressure_date?: string;
  blood_pressure_time?: string;
  systolic_mm_hg: number;
  diastolic_mm_hg: number;
  note?: string | null;
};

export type CreateExerciseInput = {
  exercise_date: string;
  exercise_time: string;
  exercise_type: ExerciseType;
  duration_min: number;
  intensity?: string | null;
  note?: string | null;
};

type DateParams = { from?: string; to?: string };

const dateQuery = (params?: DateParams) => ({
  date_since: params?.from,
  date_until: params?.to,
});

/** Best-effort row-shape coercion. Tolerates row keys we don't recognise. */
const coerceWeightRow = (row: Record<string, unknown>): WeightEntry | null => {
  const date =
    (row.weight_date as string | undefined) ??
    (row.date as string | undefined) ??
    (row.measurement_date as string | undefined);
  const weight = Number(row.weight_kg ?? row.weight ?? row.value);
  if (!date || Number.isNaN(weight)) return null;
  return {
    date,
    weight,
    unit: 'kg',
    note: (row.note as string | null | undefined) ?? null,
  };
};

const coerceBloodPressureRow = (row: Record<string, unknown>): BloodPressureEntry | null => {
  const date =
    (row.blood_pressure_date as string | undefined) ??
    (row.date as string | undefined) ??
    (row.measurement_date as string | undefined);
  const systolic = Number(row.systolic_mm_hg ?? row.systolic);
  const diastolic = Number(row.diastolic_mm_hg ?? row.diastolic);
  if (!date || Number.isNaN(systolic) || Number.isNaN(diastolic)) return null;
  return {
    date,
    systolic,
    diastolic,
    note: (row.note as string | null | undefined) ?? null,
  };
};

const safeReadHealthDataTable = async <T>(
  patientId: number,
  category: string,
  coerce: (row: Record<string, unknown>) => T | null,
  params?: DateParams,
): Promise<T[]> => {
  try {
    const res = await apiClient.get<{ rows?: Array<Record<string, unknown>> }>(
      `/patients/${patientId}/health_data_table`,
      { params: { category, ...dateQuery(params) } },
    );
    const rows = Array.isArray(res.data?.rows) ? res.data.rows : [];
    return rows.map(coerce).filter((r): r is T => r !== null);
  } catch (err) {
    const status = (err as { response?: { status?: number } }).response?.status;
    // 400/422 = the endpoint rejected the request shape. `health_data_table`
    // currently 400s on the `date_since`/`date_until` params we send (same
    // in-flight behaviour as `meals_table`, see FOLLOW_UPS). The read side is
    // best-effort, so degrade to empty rather than letting the Dashboard
    // query throw + retry-storm. Root-cause fix (correct param shape) tracked
    // in FOLLOW_UPS.
    if (status === 403 || status === 404 || status === 400 || status === 422) return [];
    throw err;
  }
};

export const dashboardApi = {
  async weight(patientId: number, params?: DateParams) {
    return safeReadHealthDataTable<WeightEntry>(patientId, 'weight', coerceWeightRow, params);
  },

  async bloodPressure(patientId: number, params?: DateParams) {
    return safeReadHealthDataTable<BloodPressureEntry>(
      patientId,
      'blood_pressure',
      coerceBloodPressureRow,
      params,
    );
  },

  async exercises(patientId: number, params?: DateParams) {
    const res = await apiClient.get<ExerciseEntry[]>(`/patients/${patientId}/exercises`, {
      params: dateQuery(params),
    });
    return Array.isArray(res.data) ? res.data : [];
  },

  /**
   * Append a weight measurement. Wraps the bulk health-data PUT endpoint
   * (swagger.yaml:11133) with the discriminator shape.
   */
  async addWeight(patientId: number, body: CreateWeightInput) {
    await apiClient.put(`/patients/${patientId}/health_data/bulk`, [
      {
        type: 'weight',
        updates: [body],
        deleted: [],
      },
    ]);
  },

  /** Append a blood pressure measurement (same bulk endpoint as addWeight). */
  async addBloodPressure(patientId: number, body: CreateBloodPressureInput) {
    await apiClient.put(`/patients/${patientId}/health_data/bulk`, [
      {
        type: 'blood_pressure',
        updates: [body],
        deleted: [],
      },
    ]);
  },

  /** POST /patients/{id}/exercise — singular, distinct from the GET /exercises path. */
  async addExercise(patientId: number, body: CreateExerciseInput) {
    const res = await apiClient.post<ExerciseEntry>(`/patients/${patientId}/exercise`, body);
    return res.data;
  },
};
